/**
 * SmarterHoops Privacy Policy.
 *
 * Build 194 update:
 *   - Full content replacement to match the Sign-in-with-Apple identity
 *     model (no more device-UUID language) and current data collection
 *     practices (gender/age_division, link codes, aggregated analytics,
 *     EU/UK/CA rights). Effective date stamped May 12, 2026.
 *   - Scroll fix: the page is now its own scroll container. The global
 *     `body { position: fixed; overflow: hidden }` lock from index.css
 *     was clipping `min-h-screen` content with no scrollbar — a hard
 *     fail for App Store reviewers. Outer element is `h-screen
 *     overflow-y-scroll` with `-webkit-overflow-scrolling: touch` so
 *     iOS Safari momentum-scrolls correctly inside the locked body.
 *
 * Section header style is preserved verbatim from the previous version
 * (orange uppercase tracking, slate body) per the design ask.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const SECTIONS = [
  {
    id: 'overview',
    title: 'Overview',
    body: (
      <>
        <p>
          SmarterHoops ("we," "us," or "the App") is a basketball
          stat-tracking application built for coaches, team managers, and
          parents. This Privacy Policy explains what data we collect, how
          we use it, how long we keep it, and the rights you have over it.
          By using SmarterHoops you agree to the practices described here.
        </p>
      </>
    ),
  },
  {
    id: 'data-we-collect',
    title: 'Data We Collect',
    body: (
      <>
        <p>
          <strong>Account data.</strong> SmarterHoops uses Sign in with
          Apple to authenticate you. When you first sign in, Apple provides
          us with:
        </p>
        <ul className="list-disc pl-6 space-y-1.5 mt-2">
          <li>
            An opaque subject identifier (a unique, persistent string that
            we use as the primary key for your account — this enables your
            data to sync automatically across all devices signed in with
            the same Apple ID)
          </li>
          <li>
            Your name (only on first sign-in, with your permission via the
            iOS authorization sheet)
          </li>
          <li>
            An email address — either your real email or a private relay
            address Apple generates, depending on which option you select
            during sign-in. If you choose "Hide My Email," Apple generates
            a relay address that forwards messages to you without revealing
            your actual email to us
          </li>
        </ul>
        <p className="mt-3">
          We never receive your Apple ID password or any other Apple
          account credentials.
        </p>
        <p className="mt-3">
          <strong>Team and game data.</strong> Anything you enter into the
          App — team rosters, jersey numbers, player names, gender, age
          divisions, games, shots, drills, practice plans, and goals — is
          stored on our servers so you can use the App across sessions and
          devices. We may also use this data in aggregate and anonymized
          form to improve app features, generate insights, develop new
          analytics tools, and benchmark performance across teams and
          divisions. Aggregated data cannot be used to identify individual
          users, players, or teams.
        </p>
        <p className="mt-3">
          Gender, age division, and similar roster fields are used only to
          organize teams, players, divisions, and basketball-related
          statistics inside the App. They are not used for advertising,
          cross-app tracking, unrelated profiling, or unrelated analytics.
        </p>
        <p className="mt-3">
          <strong>Player link codes.</strong> When a parent enters an
          8-character link code to view a player's stats, we associate that
          player profile with the parent's account. Parent-linked accounts
          can view only the player profile, statistics, goals, practice
          plans, or team information made available through the link-code
          feature. Coaches and team managers control which team or player
          information is entered and shared through the App. No additional
          personal information is collected through this flow.
        </p>
        <p className="mt-3">
          <strong>Diagnostic data.</strong> Bug reports you submit through
          the in-app Help menu, plus basic request logs (timestamp, route,
          status code) used to keep the App reliable and detect abuse.
        </p>
        <p className="mt-3">
          <strong>What We Do NOT Collect.</strong> We never receive your
          Apple ID password or any other Apple account credentials. We do
          not collect or store player, profile, or team photos. We do not
          run third-party advertising SDKs. We do not sell your data. We do
          not track you across other apps or websites. We do not use your
          data to train external AI models. We do not collect precise
          location, contacts, or device sensor data.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-use-data',
    title: 'How We Use Your Data',
    body: (
      <>
        <p>We use the data you provide solely to:</p>
        <ul className="list-disc pl-6 space-y-1.5 mt-2">
          <li>Authenticate you via Sign in with Apple and maintain your session</li>
          <li>Render your dashboards, stats, game history, and practice plans</li>
          <li>Sync your roster and game data across your devices</li>
          <li>Generate practice plan recommendations from your team's game stats</li>
          <li>Respond to bug reports and support requests</li>
          <li>Detect abuse and keep the service operational</li>
        </ul>
      </>
    ),
  },
  {
    id: 'storage-and-security',
    title: 'Storage And Security',
    body: (
      <>
        <p>
          Account and game data is stored in MongoDB on secured cloud
          infrastructure operated by Emergent. Emergent operates the
          underlying database infrastructure but does not independently
          access your data outside of providing standard hosting services.
          All traffic between the App and our servers is encrypted in
          transit using TLS. Your Apple identity is verified using Apple's
          public JWKS endpoint, and your session token (JWT) is stored in
          your device's secure Keychain and is never transmitted in plain
          text.
        </p>
      </>
    ),
  },
  {
    id: 'data-retention',
    title: 'Data Retention',
    body: (
      <>
        <p>
          We retain your team and game data for as long as your account is
          active. If you delete your account from inside the App, your
          account and associated team, game, roster, and practice plan data
          are deleted from active systems promptly. Limited backup,
          security, diagnostic, or legal records may remain for a short
          period where technically necessary or legally permitted, but they
          are not used for active App functionality. As part of account
          deletion, we also revoke our access to your Apple ID via Apple's
          official revocation endpoint, as required by Apple's App Store
          guidelines. Anonymized, aggregated diagnostic logs may be
          retained longer for service-reliability purposes but cannot be
          linked back to you.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Sharing',
    body: (
      <>
        <p>
          We do not sell your data. We share it only with the following
          processors, strictly to operate the App:
        </p>
        <ul className="list-disc pl-6 space-y-1.5 mt-2">
          <li>
            <strong>Emergent</strong> — cloud hosting for our servers and
            database
          </li>
          <li>
            <strong>Apple</strong> — App Store distribution, Sign in with
            Apple authentication, and (where applicable) push notification
            delivery
          </li>
          <li>
            <strong>YouTube</strong> — drill videos may be embedded from
            YouTube. When you view or interact with embedded YouTube
            content, Google/YouTube may collect information under its own
            privacy policy, available at{' '}
            <a
              className="underline"
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              policies.google.com/privacy
            </a>
            . We do not control YouTube's independent data practices
          </li>
        </ul>
        <p className="mt-3">
          We do not share your data with advertisers, data brokers, or
          analytics providers that profile users across services.
        </p>
        <p className="mt-3">
          If SmarterHoops is involved in a merger, acquisition,
          reorganization, or sale of assets, user data may be transferred
          as part of that transaction, subject to this Privacy Policy or a
          policy with substantially similar protections.
        </p>
      </>
    ),
  },
  {
    id: 'children-and-minors',
    title: 'Children And Minors',
    body: (
      <>
        <p>
          SmarterHoops is intended for adult coaches, team managers, and
          parents. Account creation requires Sign in with Apple, which is
          restricted by Apple's terms to users who meet Apple's minimum age
          requirements (generally 13 and older in the United States, with
          parental consent required for younger users in jurisdictions
          where applicable). We rely on Apple's age verification systems
          and the App Store's parental consent mechanisms as the primary
          safeguards against direct account creation by minors. We do not
          knowingly collect personal information directly from children
          under 13.
        </p>
        <p className="mt-3">
          SmarterHoops is a tool for recording basketball performance and
          roster data entered by coaches, parents, or team managers during
          games and practices, consistent with information typically found
          in public box scores and tournament programs. The App does not
          collect private player information, including home addresses,
          contact details, medical records, photographs, or precise
          location data.
        </p>
        <p className="mt-3">
          Roster and player data entered into SmarterHoops is user-entered
          team content, not school-record or educational data. SmarterHoops
          is not intended to be used as an official school record system,
          educational record system, or student information system.
        </p>
        <p className="mt-3">
          Player rosters managed inside a team may include minors. As the
          coach, team manager, or parent entering roster information, you
          are responsible for obtaining any consent required by applicable
          laws, including laws related to children's privacy, student data,
          youth sports, school-affiliated teams, or similar state-level
          privacy requirements, before adding a minor's name, jersey
          number, or other identifying information to your roster.
        </p>
        <p className="mt-3">
          If you are a parent or guardian and believe a minor's data has
          been added to the App without your consent, please email{' '}
          <a className="underline" href="mailto:support@smarterhoops.com">
            support@smarterhoops.com
          </a>{' '}
          and we will remove the data immediately upon verification.
        </p>
      </>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    body: (
      <>
        <p>You can, at any time:</p>
        <ul className="list-disc pl-6 space-y-1.5 mt-2">
          <li>
            Edit or delete any team, game, player, or practice plan from
            inside the App
          </li>
          <li>
            Delete your account directly inside the App by going to
            Settings → Delete Account. This action is permanent, erases all
            your team and game data from active systems promptly, and
            revokes our access to your Apple ID
          </li>
          <li>
            Sign out of the App at any time from Settings, which clears
            your session locally
          </li>
          <li>
            Email us at{' '}
            <a className="underline" href="mailto:support@smarterhoops.com">
              support@smarterhoops.com
            </a>{' '}
            to request access to, correction of, deletion of, or a copy of
            personal data associated with your account, subject to
            verification and applicable law
          </li>
        </ul>
        <p className="mt-3">
          To protect account and player information, we may need to verify
          your identity or account ownership before completing privacy,
          access, export, correction, or deletion requests.
        </p>
      </>
    ),
  },
  {
    id: 'eu-uk-california',
    title: 'EU, UK, And California Users',
    body: (
      <>
        <p>
          If you are in the European Union, United Kingdom, or California,
          you have additional rights under GDPR, UK GDPR, or the CCPA —
          including the rights to access, correct, port, and erase your
          personal data, and to object to or restrict certain processing.
          To exercise these rights, email{' '}
          <a className="underline" href="mailto:support@smarterhoops.com">
            support@smarterhoops.com
          </a>
          . We will respond within 30 days.
        </p>
        <p className="mt-3">
          We do not "sell" personal information as defined under the CCPA.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes To This Policy',
    body: (
      <>
        <p>
          We may update this policy as the App evolves. Material changes
          will be communicated through the App or by email (when an email
          is on file) at least 7 days before they take effect. Continued
          use of the App after the effective date of an updated policy
          constitutes acceptance of the changes.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <>
        <p>
          Questions, requests, or complaints? Email{' '}
          <a className="underline" href="mailto:support@smarterhoops.com">
            support@smarterhoops.com
          </a>
          . We aim to respond within two business days.
        </p>
        <p className="mt-3 text-sm text-slate-500">
          SmarterHoops is operated by Chance Reaves as a sole proprietor.
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div
      data-testid="privacy-policy-page"
      className="h-screen w-full overflow-y-scroll bg-white text-slate-900"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold tracking-[.08em] uppercase text-slate-600 hover:text-slate-900 transition-colors"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <h1
          className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
          data-testid="privacy-title"
        >
          SmarterHoops Privacy Policy
        </h1>
        <p className="mt-3 text-base text-slate-600">
          How SmarterHoops handles your account, team, and game data.
        </p>
        <p className="mt-1 text-sm text-slate-500" data-testid="privacy-effective-date">
          <strong>Effective date:</strong> May 12, 2026
        </p>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`}>
              <h2 className="text-base md:text-lg font-bold tracking-[.08em] uppercase text-orange-600">
                {s.title}
              </h2>
              <div className="mt-3 text-base leading-relaxed text-slate-700 space-y-2">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div
          className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-500"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 3rem)' }}
        >
          SmarterHoops · Bundle ID{' '}
          <code className="text-slate-700">com.smarterhoops.app</code>
        </div>
      </div>
    </div>
  );
}
